import type { Rng } from '@src/core/Rng';
import { spawnFx } from '@src/ecs/archetypes';
import { ClipId, EnemyKind, SpriteId } from '@src/ecs/components';
import { emitParticles, ParticleKind, requestDecal, type GameContext } from '@src/ecs/world';
import { Anims } from '@src/game/AnimDefs';
import { BossBody } from '@src/game/BossDefs';

const IMP_GOO = 0xc44fa8;
const IMP_FLESH = 0x7a1838;
const GOLEM_DUST = 0x9aa7b3;
const SLIME_GOO = 0x6fe03a;
const SKULL_FIRE = 0x7dfff0;
const BAT_SPARK = 0xc07aff;
const BAT_ICHOR = 0x4a1848;
const SPITTER_ACID = 0x8cff3a;
const HEXER_INK = 0x2fd8e8;
const CINDER_ASH = 0x3a2214;
const BLIGHT_GOO = 0xa64cff;
const WRAITH_MIST = 0xb8ccff;
const BRUTE_EMBER = 0xff6a3a;
const BRUTE_BLOOD = 0x5a1410;
const MIMIC_GOLD = 0xffd94a;
const MIMIC_TOOTH = 0xf0e6d0;
const STONE = 0x9a8b78;

/** Scratch sampled by the renderer each dying body. No allocation in the draw loop. */
export const deathVisual = {
  yOff: 0,
  rot: 0,
  scale: 1,
  squash: 0,
  alpha: 1,
  tintMix: 0,
  tint: 0xffffff,
  shadow: 1,
};

/** Three looks per kind so a pack of the same fiend does not pop identically. */
export function pickDeathVariant(rng: Rng): number {
  return rng.int(0, 2);
}

/** Linger on the last splat frame a beat past the clip so pickups stay readable. */
export function deathHoldSeconds(kind: EnemyKind): number {
  switch (kind) {
    case EnemyKind.Golem:
    case EnemyKind.Brute:
      return 0.68;
    case EnemyKind.Wraith:
    case EnemyKind.Mimic:
      return 0.56;
    case EnemyKind.Blight:
    case EnemyKind.Gargoyle:
    case EnemyKind.Spitter:
      return 0.52;
    default:
      return 0.48;
  }
}

function stain(ctx: GameContext, x: number, y: number, tint: number, scale: number, n: number): void {
  const rng = ctx.rng;
  for (let i = 0; i < n; i++) {
    requestDecal(ctx.fx, x + rng.range(-0.55, 0.55), y + rng.range(-0.4, 0.4), tint, scale * rng.range(0.72, 1.12));
  }
}

function aim(variant: number, vx: number, vy: number): { dx: number; dy: number } {
  if (variant === 1) return { dx: vx, dy: vy };
  if (variant === 2) return { dx: 0, dy: 0 };
  return { dx: vx * 0.65, dy: vy * 0.65 };
}

/**
 * Particles, stains and one-shot sprites for a small-fry kill. Drops and
 * gameplay (cinder blast, blight split) stay in DamageSystem.
 */
export function emitKillGore(ctx: GameContext, kind: EnemyKind, x: number, y: number, vx: number, vy: number, variant: number): void {
  const { dx, dy } = aim(variant, vx, vy);
  const messy = variant === 2;
  const rng = ctx.rng;

  switch (kind) {
    case EnemyKind.Golem: {
      stain(ctx, x, y, GOLEM_DUST, 1.55, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, dx, dy, 1.15);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, messy ? 1.1 : 0.85, GOLEM_DUST);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, 0.95, 0xb9c4cf);
      emitParticles(ctx.fx, ParticleKind.AshFall, x, y, 0, 1, 0.45, 0x6a6460);
      spawnFx(ctx, SpriteId.Smoke, x, y, rng.next() * Math.PI * 2, 2.2, 0xb9c4cf, 0.36);
      break;
    }
    case EnemyKind.Skull: {
      emitParticles(ctx.fx, ParticleKind.BoneSpray, x, y, dx, dy, messy ? 1.45 : 1.2);
      emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, dx, dy, 1.05, SKULL_FIRE);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, 0.7, 0xd8e6dd);
      if (messy) emitParticles(ctx.fx, ParticleKind.AshFall, x, y, 0, 1, 0.4, 0xc8e0d8);
      break;
    }
    case EnemyKind.Slime: {
      stain(ctx, x, y, SLIME_GOO, 0.95 + rng.next() * 0.3, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1.25, SLIME_GOO);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, dx, dy, 1.15, SLIME_GOO);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.7, 0x4aa018);
      spawnFx(ctx, SpriteId.Goo, x, y, rng.next() * Math.PI * 2, messy ? 1.45 : 1.2, 0x9cff5a, 0.31);
      break;
    }
    case EnemyKind.Bat: {
      emitParticles(ctx.fx, ParticleKind.MistTear, x, y, dx, dy, 1.05, BAT_SPARK);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 0.55, BAT_ICHOR);
      emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, dx, dy, 0.7, BAT_SPARK);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, 0.5, 0x6a4a78);
      if (messy) emitParticles(ctx.fx, ParticleKind.Confetti, x, y, 0, 1, 0.22);
      break;
    }
    case EnemyKind.Gargoyle: {
      stain(ctx, x, y, STONE, 1.1, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, dx, dy, 1);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.8, STONE);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, 0.85, 0xc4b59a);
      emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 1, 0.45, 0xffc14a);
      spawnFx(ctx, SpriteId.Smoke, x, y, rng.next() * Math.PI * 2, 1.6, 0xc4b59a, 0.34);
      break;
    }
    case EnemyKind.Spitter: {
      stain(ctx, x, y, 0x5d7a3a, 1.15, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, dx, dy, 0.75);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1.1, SPITTER_ACID);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, dx, dy, 0.95, SPITTER_ACID);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.5, 0x6a8a3a);
      spawnFx(ctx, SpriteId.Goo, x, y, rng.next() * Math.PI * 2, 1.4, SPITTER_ACID, 0.31);
      break;
    }
    case EnemyKind.Hexer: {
      stain(ctx, x, y, 0x1f4c66, 0.55 + rng.next() * 0.25, messy ? 2 : 1);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1, HEXER_INK);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, dx, dy, 0.85, HEXER_INK);
      emitParticles(ctx.fx, ParticleKind.MistTear, x, y, 0, 1, 0.55, HEXER_INK);
      emitParticles(ctx.fx, ParticleKind.MuzzleSparks, x, y, 0, 1, 0.55, HEXER_INK);
      break;
    }
    case EnemyKind.Cinder: {
      emitParticles(ctx.fx, ParticleKind.BoneSpray, x, y, dx, dy, 1.1);
      emitParticles(ctx.fx, ParticleKind.AshFall, x, y, 0, 1, 1.4, CINDER_ASH);
      emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 1, 0.7, 0xff8a30);
      break;
    }
    case EnemyKind.Blight: {
      stain(ctx, x, y, BLIGHT_GOO, 1.35 + rng.next() * 0.25, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1.45, BLIGHT_GOO);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, dx, dy, 1.35, BLIGHT_GOO);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.85, 0x6a2a88);
      spawnFx(ctx, SpriteId.Goo, x, y, rng.next() * Math.PI * 2, 1.6, 0xd39cff, 0.31);
      ctx.fx.shakeY += 0.12;
      break;
    }
    case EnemyKind.Wraith: {
      emitParticles(ctx.fx, ParticleKind.MistTear, x, y, dx * 0.4, dy * 0.4, messy ? 1.6 : 1.35, WRAITH_MIST);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, vx * 0.2, vy * 0.2, 1, WRAITH_MIST);
      emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 1, 0.5, WRAITH_MIST);
      spawnFx(ctx, SpriteId.Smoke, x, y, rng.next() * Math.PI * 2, 1.35, WRAITH_MIST, 0.3);
      if (messy) emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 0.4, 0x8aa0cc);
      break;
    }
    case EnemyKind.Brute: {
      stain(ctx, x, y, 0x4a2418, 1.5, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, dx, dy, 1.1);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1.05, BRUTE_BLOOD);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.95, 0x3a1810);
      emitParticles(ctx.fx, ParticleKind.Embers, x, y, 0, 1, 1.25, BRUTE_EMBER);
      spawnFx(ctx, SpriteId.Explosion, x, y, rng.next() * Math.PI * 2, 1.9, BRUTE_EMBER, 0.6);
      ctx.fx.shakeX += 0.2;
      ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.6);
      break;
    }
    case EnemyKind.Mimic: {
      emitParticles(ctx.fx, ParticleKind.PickupSparkle, x, y, 0, 1, 1.5, MIMIC_GOLD);
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, dx, dy, 0.65);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 1, MIMIC_TOOTH);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, messy ? 1 : 0.7, 0x6a1010);
      spawnFx(ctx, SpriteId.Shockwave, x, y, 0, 1.6, MIMIC_GOLD, 0.5);
      break;
    }
    case EnemyKind.Imp:
    default: {
      stain(ctx, x, y, IMP_GOO, 0.5 + rng.next() * 0.28, messy ? 3 : 2);
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, dx, dy, 1.05 + (messy ? 0.2 : 0), IMP_FLESH);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, dx, dy, 0.9, IMP_GOO);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, dx, dy, 0.55, IMP_FLESH);
      if (messy || rng.chance(0.55)) spawnFx(ctx, SpriteId.Goo, x, y, rng.next() * Math.PI * 2, 0.95, 0xffffff, 0.31);
      break;
    }
  }
}

/** Extra viscera on a boss kill. Keeps the crescendo's own bursts; this is the meat. */
export function emitBossKillGore(ctx: GameContext, body: BossBody, x: number, y: number, power: number): void {
  switch (body) {
    case BossBody.Goo:
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, 0, 0, power * 1.1, SLIME_GOO);
      emitParticles(ctx.fx, ParticleKind.ImpGoo, x, y, 0, 0, power * 0.9, SLIME_GOO);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, 0, 0, power * 0.7, 0x4aa018);
      if (power > 0.8) stain(ctx, x, y, SLIME_GOO, 2.2, 2);
      break;
    case BossBody.Bone:
      emitParticles(ctx.fx, ParticleKind.BoneSpray, x, y, 0, 0, power * 1.2);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, power * 0.7, 0xd8e6dd);
      emitParticles(ctx.fx, ParticleKind.AshFall, x, y, 0, 1, power * 0.5, 0xc8b48c);
      break;
    case BossBody.Stone:
      emitParticles(ctx.fx, ParticleKind.GolemRubble, x, y, 0, 0, power);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, 0, 0, power * 0.8, STONE);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, power * 0.7, 0xc4b59a);
      break;
    case BossBody.Leather:
      emitParticles(ctx.fx, ParticleKind.BloodSpray, x, y, 0, 0, power, BRUTE_BLOOD);
      emitParticles(ctx.fx, ParticleKind.GibChunk, x, y, 0, 0, power * 0.75, 0x3a1810);
      emitParticles(ctx.fx, ParticleKind.Dust, x, y, 0, 1, power * 0.55, 0x4a2418);
      if (power > 0.8) stain(ctx, x, y, 0x4a2418, 2.4, 2);
      break;
    default:
      emitParticles(ctx.fx, ParticleKind.MistTear, x, y, 0, 1, power, 0xc8b0ff);
      emitParticles(ctx.fx, ParticleKind.HitSpark, x, y, 0, 0, power * 0.7, 0xe8d8ff);
      emitParticles(ctx.fx, ParticleKind.BoneSpray, x, y, 0, 0, power * 0.4);
      break;
  }
}

function fadeFrom(ownClip: boolean, late: number, early: number): number {
  return ownClip ? late : early;
}

function applyFade(u: number, from: number): void {
  deathVisual.alpha = u < from ? 1 : Math.max(0, 1 - (u - from) / Math.max(0.04, 1 - from));
}

/**
 * Corpse pose for the current death progress `u` (0..1). Clip-backed kinds stay
 * milder so the sheet still reads; wraiths and mimics are all pose.
 */
export function sampleDeathVisual(kind: EnemyKind, sprite: SpriteId, variant: number, seed: number, u: number): void {
  const own = Anims.hasOwnClip(sprite, ClipId.Death);
  const side = seed > 0.5 ? 1 : -1;
  const v = variant | 0;
  deathVisual.yOff = 0;
  deathVisual.rot = 0;
  deathVisual.scale = 1;
  deathVisual.squash = 0;
  deathVisual.alpha = 1;
  deathVisual.tintMix = 0;
  deathVisual.tint = 0xffffff;
  deathVisual.shadow = Math.max(0, 1 - u * 1.35);

  switch (kind) {
    case EnemyKind.Slime:
    case EnemyKind.Blight: {
      const melt = u * u;
      const k = own ? 0.45 : 1;
      deathVisual.squash = (0.22 + melt * 1.15) * k;
      deathVisual.yOff = -melt * 0.22 * k;
      deathVisual.scale = 1 + (1 - u) * 0.06;
      deathVisual.tint = kind === EnemyKind.Blight ? BLIGHT_GOO : SLIME_GOO;
      deathVisual.tintMix = 0.12 + u * 0.32;
      applyFade(u, fadeFrom(own, 0.8, 0.72));
      break;
    }
    case EnemyKind.Skull:
    case EnemyKind.Cinder: {
      deathVisual.tint = kind === EnemyKind.Cinder ? 0xff8a30 : SKULL_FIRE;
      deathVisual.tintMix = 0.18 + u * 0.38;
      if (own) {
        deathVisual.scale = 1 + (v === 2 ? Math.sin(u * Math.PI) * 0.1 : 0);
        deathVisual.rot = side * u * 0.4;
        applyFade(u, 0.74);
      } else {
        deathVisual.scale = Math.max(0.2, u < 0.2 ? 1.15 : 1.15 - (u - 0.2) * 1.15);
        deathVisual.rot = side * u * (2.4 + v * 0.7);
        applyFade(u, 0.5);
      }
      break;
    }
    case EnemyKind.Imp:
    case EnemyKind.Hexer: {
      deathVisual.squash = (v === 1 ? 0.16 : 0.34) * u * (own ? 0.45 : 1);
      deathVisual.rot = side * u * (own ? 0.18 : 0.7);
      deathVisual.yOff = -u * 0.08;
      if (!own) deathVisual.scale = u < 0.2 ? 1 + u * 1.2 : 1.12 - (u - 0.2) * 0.25;
      deathVisual.tint = kind === EnemyKind.Hexer ? HEXER_INK : IMP_GOO;
      deathVisual.tintMix = 0.1 + u * 0.22;
      applyFade(u, fadeFrom(own, 0.78, 0.64));
      break;
    }
    case EnemyKind.Golem:
    case EnemyKind.Brute: {
      const k = own ? 0.4 : 1;
      deathVisual.rot = side * u * (own ? 0.14 : 1.05);
      deathVisual.squash = u * 0.55 * k;
      deathVisual.yOff = -u * 0.18 * k;
      deathVisual.tint = kind === EnemyKind.Brute ? BRUTE_EMBER : GOLEM_DUST;
      deathVisual.tintMix = u * 0.22;
      applyFade(u, 0.82);
      break;
    }
    case EnemyKind.Gargoyle:
    case EnemyKind.Spitter: {
      const k = own ? 0.35 : 1;
      deathVisual.squash = u * 0.5 * k;
      deathVisual.scale = 1 - u * 0.14 * k;
      deathVisual.rot = side * u * (own ? 0.12 : 0.55);
      deathVisual.tint = kind === EnemyKind.Spitter ? SPITTER_ACID : STONE;
      deathVisual.tintMix = 0.12 + u * 0.2;
      applyFade(u, 0.8);
      break;
    }
    case EnemyKind.Bat: {
      deathVisual.rot = side * u * (own ? 0.45 : 3.1);
      deathVisual.scale = own ? 1 : Math.max(0.15, 1.08 - u * 1.02);
      deathVisual.yOff = own ? 0 : -u * 0.32;
      deathVisual.tint = BAT_SPARK;
      deathVisual.tintMix = u * 0.28;
      applyFade(u, fadeFrom(own, 0.72, 0.45));
      break;
    }
    case EnemyKind.Wraith: {
      const flicker = Math.sin(u * 42 + seed * 20) * 0.5 + 0.5;
      deathVisual.shadow = 0;
      deathVisual.tint = WRAITH_MIST;
      deathVisual.tintMix = 0.35 + u * 0.4;
      if (v === 1) {
        deathVisual.squash = 0.22 + u * 0.85;
        deathVisual.scale = 1 + u * 0.18;
        deathVisual.alpha = (1 - u * 0.95) * (0.5 + flicker * 0.5);
      } else if (v === 2) {
        deathVisual.scale = Math.max(0.18, 1.18 - u * 1.12);
        deathVisual.rot = side * Math.sin(u * 10) * 0.35;
        deathVisual.alpha = (1 - u) * (0.45 + flicker * 0.55);
      } else {
        deathVisual.squash = -0.35 - u * 0.55;
        deathVisual.scale = 1 + u * 0.35;
        deathVisual.yOff = (1 - u) * 0.22;
        deathVisual.rot = side * Math.sin(u * 9) * 0.25;
        deathVisual.alpha = (1 - u * 0.95) * (0.55 + flicker * 0.45);
      }
      break;
    }
    case EnemyKind.Mimic: {
      if (u < 0.28) {
        const s = u / 0.28;
        deathVisual.squash = 0.4 + s * 0.7;
        deathVisual.scale = 1 - s * 0.15;
      } else {
        const s = (u - 0.28) / 0.72;
        deathVisual.squash = 0.2 - s * 0.35;
        deathVisual.scale = 0.85 + Math.sin(s * Math.PI) * 0.32;
        deathVisual.rot = side * s * 0.75;
      }
      deathVisual.tint = v === 2 ? 0x8a1a1a : MIMIC_GOLD;
      deathVisual.tintMix = 0.2 + u * 0.32;
      applyFade(u, 0.7);
      break;
    }
    default:
      applyFade(u, 0.7);
      break;
  }
}
